/**
 * SkeletonLoader — Full-page skeleton screens for each page type.
 * Shows animated shimmer placeholders that match the real layout.
 * Minimum display time: 2 seconds (enforced in AppWithSkeleton).
 */

/* ── Reusable Skeleton Primitives ────────────────────── */

function SkeletonBox({ className = '', style = {} }) {
  return <div className={`skeleton ${className}`} style={style} />;
}

function SkeletonText({ width = '100%', className = '' }) {
  return <div className={`skeleton-text ${className}`} style={{ width }} />;
}

function SkeletonHeading({ width = '60%', className = '' }) {
  return <div className={`skeleton-heading ${className}`} style={{ width }} />;
}

function SkeletonCircle({ size = 32, className = '' }) {
  return (
    <div
      className={`skeleton-circle ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/* ── Shell Skeleton (Header + StatusBar) ─────────────── */

function ShellSkeleton() {
  return (
    <>
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-divider px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SkeletonBox className="w-8 h-8 rounded-lg" />
          <SkeletonBox className="w-28 h-6 rounded" />
        </div>
        <div className="flex items-center gap-3">
          <SkeletonBox className="w-20 h-7 rounded-full" />
          <SkeletonCircle size={32} />
        </div>
      </header>

      {/* StatusBar */}
      <div className="sticky top-0 z-50 w-full">
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-divider">
          <div className="flex items-center gap-2">
            <SkeletonCircle size={12} />
            <SkeletonBox className="w-14 h-4 rounded" />
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Login Page Skeleton ─────────────────────────────── */

export function LoginSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <SkeletonBox className="w-20 h-20 rounded-2xl" />
          </div>
          <SkeletonBox className="mx-auto w-40 h-10 mb-2 rounded" />
          <SkeletonHeading width="70%" className="mx-auto" />
          <SkeletonText width="55%" className="mx-auto" />
        </div>

        {/* Form */}
        <div className="space-y-4">
          <div>
            <SkeletonText width="30%" />
            <SkeletonBox className="w-full h-12 rounded-lg" />
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <SkeletonText width="25%" />
              <SkeletonText width="30%" />
            </div>
            <SkeletonBox className="w-full h-12 rounded-lg" />
          </div>
          <SkeletonBox className="w-full h-12 rounded-lg" />
        </div>

        <div className="flex justify-center mt-8">
          <SkeletonText width="60%" />
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard Page Skeleton ─────────────────────────── */

export function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <ShellSkeleton />
      <main className="flex-1">
        {/* Welcome Banner */}
        <div className="bg-indigo-600/10">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <SkeletonBox className="w-72 h-9 mb-2 rounded !bg-indigo-200/40" />
            <SkeletonBox className="w-48 h-5 rounded !bg-indigo-200/30" />
          </div>
        </div>

        {/* Stats */}
        <div className="max-w-5xl mx-auto px-4 -mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white border border-divider rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <SkeletonBox className="w-10 h-10 rounded-lg" />
                  <SkeletonText width="50%" />
                </div>
                <SkeletonBox className="w-16 h-9 rounded" />
                {i === 3 && (
                  <div className="flex gap-2 mt-4">
                    <SkeletonBox className="flex-1 h-9 rounded-lg" />
                    <SkeletonBox className="flex-1 h-9 rounded-lg" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content area */}
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Assigned Classes */}
          <div className="lg:col-span-2">
            <SkeletonHeading width="40%" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white border border-divider rounded-xl p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <SkeletonBox className="w-32 h-6 mb-2 rounded" />
                      <SkeletonText width="40%" />
                    </div>
                    <SkeletonCircle size={32} />
                  </div>
                  <div className="flex items-center gap-2">
                    <SkeletonCircle size={14} />
                    <SkeletonText width="35%" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <SkeletonHeading width="60%" />
            <div className="bg-white border border-divider rounded-xl p-5">
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-start gap-3">
                    <SkeletonCircle size={32} />
                    <div className="flex-1">
                      <SkeletonText width="80%" />
                      <SkeletonText width="40%" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ── Attendance Page Skeleton ────────────────────────── */

export function AttendanceSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <ShellSkeleton />
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 py-4">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <SkeletonBox className="w-11 h-11 rounded-lg" />
            <div>
              <SkeletonHeading width="200px" />
              <SkeletonText width="140px" />
            </div>
          </div>

          {/* Date Picker */}
          <div className="mb-6">
            <SkeletonText width="40px" />
            <SkeletonBox className="w-48 h-12 rounded-lg" />
          </div>

          {/* Student List */}
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="flex items-center justify-between bg-white border border-divider rounded-xl px-4 py-3">
                <div>
                  <SkeletonBox className="w-36 h-5 mb-1 rounded" />
                  <SkeletonText width="80px" />
                </div>
                <div className="flex items-center gap-2">
                  <SkeletonBox className="w-12 h-12 rounded-lg" />
                  <SkeletonBox className="w-12 h-12 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ── Marks Page Skeleton ─────────────────────────────── */

export function MarksSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <ShellSkeleton />
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <SkeletonBox className="w-11 h-11 rounded-lg" />
              <div>
                <SkeletonText width="200px" />
                <SkeletonHeading width="160px" />
              </div>
            </div>
            <div className="flex gap-2">
              <SkeletonBox className="w-28 h-10 rounded-lg" />
              <SkeletonBox className="w-28 h-10 rounded-lg" />
            </div>
          </div>

          {/* Selectors */}
          <div className="bg-white border border-divider rounded-xl p-4 mb-6">
            <div className="flex gap-4 items-end">
              <div>
                <SkeletonText width="80px" />
                <SkeletonBox className="w-48 h-10 rounded-lg" />
              </div>
              <SkeletonText width="160px" />
            </div>
          </div>

          {/* Grid */}
          <div className="border border-divider rounded-xl bg-white shadow-sm overflow-hidden">
            {/* Header Row */}
            <div className="flex items-center border-b border-divider bg-gray-50 px-4 py-3">
              <div className="w-56 flex-shrink-0">
                <SkeletonText width="140px" />
              </div>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-32 flex-shrink-0 text-center px-2">
                  <SkeletonText width="60px" className="mx-auto" />
                </div>
              ))}
            </div>
            {/* Data Rows */}
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center border-b border-divider px-4 py-2">
                <div className="w-56 flex-shrink-0 pr-4 flex items-center gap-3">
                  <SkeletonCircle size={32} />
                  <SkeletonText width="100px" />
                </div>
                {[1, 2, 3, 4].map(j => (
                  <div key={j} className="w-32 flex-shrink-0 flex justify-center px-2">
                    <SkeletonBox className="w-full h-12 rounded-md" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ── Admin Dashboard Skeleton ────────────────────────── */

export function AdminDashboardSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <ShellSkeleton />
      <main className="flex-1">
        {/* Welcome Banner */}
        <div className="bg-indigo-900/10">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <SkeletonBox className="w-80 h-9 mb-2 rounded !bg-indigo-200/30" />
            <SkeletonBox className="w-56 h-5 rounded !bg-indigo-200/20" />
          </div>
        </div>

        {/* System Health */}
        <div className="max-w-6xl mx-auto px-4 -mt-8 mb-8">
          <div className="bg-white border border-divider rounded-xl p-5 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex flex-col px-4">
                <div className="flex items-center gap-2 mb-2">
                  <SkeletonCircle size={18} />
                  <SkeletonText width="80px" />
                </div>
                <SkeletonBox className="w-20 h-8 rounded" />
                <SkeletonText width="100px" className="mt-1" />
              </div>
            ))}
          </div>
        </div>

        {/* Action Cards */}
        <div className="max-w-6xl mx-auto px-4">
          <SkeletonHeading width="45%" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white border border-divider rounded-xl p-5">
                <SkeletonBox className="w-12 h-12 rounded-lg mb-4" />
                <SkeletonBox className="w-32 h-6 mb-1 rounded" />
                <SkeletonText width="90%" />
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ── Generic Page Skeleton (fallback) ────────────────── */

export function GenericPageSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <ShellSkeleton />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Page Header */}
          <div className="flex items-center gap-4 mb-8">
            <SkeletonBox className="w-11 h-11 rounded-lg" />
            <div>
              <SkeletonHeading width="200px" />
              <SkeletonText width="280px" />
            </div>
          </div>

          {/* Content Cards */}
          <div className="space-y-6">
            {[1, 2].map(i => (
              <div key={i} className="bg-white border border-divider rounded-xl p-6 shadow-sm">
                <SkeletonBox className="w-48 h-6 mb-4 rounded" />
                <div className="space-y-4">
                  <div>
                    <SkeletonText width="25%" />
                    <SkeletonBox className="w-full h-12 rounded-lg" />
                  </div>
                  <div>
                    <SkeletonText width="30%" />
                    <SkeletonBox className="w-full h-12 rounded-lg" />
                  </div>
                </div>
              </div>
            ))}

            {/* Action Area */}
            <div className="flex justify-end">
              <SkeletonBox className="w-36 h-12 rounded-lg" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ── Full App Skeleton (shown on initial load) ───────── */

export function AppSkeleton() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <ShellSkeleton />
      <main className="flex-1">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-indigo-100/60 to-indigo-50/40">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <SkeletonBox className="w-72 h-9 mb-2 rounded" />
            <SkeletonBox className="w-48 h-5 rounded" />
          </div>
        </div>

        {/* Stats */}
        <div className="max-w-5xl mx-auto px-4 -mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white border border-divider rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <SkeletonBox className="w-10 h-10 rounded-lg" />
                  <SkeletonText width="50%" />
                </div>
                <SkeletonBox className="w-20 h-9 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-8 pb-8">
          <div className="lg:col-span-2">
            <SkeletonHeading width="40%" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white border border-divider rounded-xl p-5">
                  <SkeletonBox className="w-28 h-6 mb-2 rounded" />
                  <SkeletonText width="40%" />
                  <div className="flex items-center gap-2 mt-4">
                    <SkeletonCircle size={14} />
                    <SkeletonText width="35%" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SkeletonHeading width="60%" />
            <div className="bg-white border border-divider rounded-xl p-5 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-start gap-3">
                  <SkeletonCircle size={28} />
                  <div className="flex-1">
                    <SkeletonText width="80%" />
                    <SkeletonText width="40%" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
